import React, { useMemo } from 'react';
import { Grid, Box, Alert, Typography, CircularProgress, Chip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DayTags from './DayTags';
import SlotTags from './SlotTags';
import TimePicker from './TimePicker';
import useFetch from '../../../../hooks/useFetch';
import { toPersianDigits } from '../../../../utils/convertNumbers';

export default function DayTimeSelection({
    studentId,
    teacherCourseId,
    selectedDay,
    selectedTime,
    onDaySelect,
    onTimeChange,
    selectedTeacherId,
    editRecord,
    mode,
    slotRefreshKey,
    enabled
}) {
    // Fetch teacher working hours when teacher and day are selected
    const workingHoursApiUrl = selectedTeacherId !== null && selectedDay !== '' && selectedDay !== null && selectedDay !== undefined
        ? '/institutes/api/v1/teacher-working-days/'
        : null;
    
    // Memoize params to prevent infinite loops
    const workingHoursParams = useMemo(() => {
        if (!workingHoursApiUrl) return undefined;
        return {
            teacher: selectedTeacherId,
            day: selectedDay
        };
    }, [workingHoursApiUrl, selectedTeacherId, selectedDay]);
    
    const { data: workingHoursData, loading: workingHoursLoading } = useFetch(
        workingHoursApiUrl,
        {
            params: workingHoursParams,
            skip: !workingHoursApiUrl
        }
    );

    // Extract working hours from API response
    const workingHours = workingHoursData?.results?.[0] || workingHoursData?.[0] || null;
    const effectiveStartTime = workingHours?.effective_start_time || workingHours?.start_time || null;
    const effectiveEndTime = workingHours?.effective_end_time || workingHours?.end_time || null;
    const isWorking = workingHours?.is_working !== false; // Default to true if not specified

    // Format time for display (HH:MM)
    const formatTime = (timeString) => {
        if (!timeString) return null;
        // Handle both "HH:MM:SS" and "HH:MM" formats
        return timeString.slice(0, 5);
    };

    return (
        <>
            {/* Day Tags */}
            <Grid item xs={12}>
                <DayTags
                    studentId={studentId}
                    teacherCourseId={teacherCourseId}
                    selectedDay={selectedDay}
                    onDaySelect={onDaySelect}
                    enabled={enabled}
                />
            </Grid>

            {/* Teacher Working Hours Display */}
            {selectedTeacherId && selectedDay !== '' && selectedDay !== null && selectedDay !== undefined && (
                <Grid item xs={12}>
                    {workingHoursLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                در حال دریافت ساعات کاری آموزگار...
                            </Typography>
                        </Box>
                    ) : isWorking && effectiveStartTime && effectiveEndTime ? (
                        <Alert 
                            severity="info" 
                            icon={<AccessTimeIcon />}
                            sx={{ 
                                '& .MuiAlert-message': {
                                    width: '100%'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" component="span">
                                    ساعات کاری آموزگار در این روز:
                                </Typography>
                                <Chip 
                                    label={`${toPersianDigits(formatTime(effectiveStartTime))} - ${toPersianDigits(formatTime(effectiveEndTime))}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                        </Alert>
                    ) : !isWorking ? (
                        <Alert severity="warning" icon={<AccessTimeIcon />}>
                            <Typography variant="body2">
                                آموزگار در این روز تعطیل است
                            </Typography>
                        </Alert>
                    ) : null}
                </Grid>
            )}

            {/* Slot Tags - Show only conflicts */}
            <Grid item xs={12}>
                <SlotTags
                    studentId={studentId || editRecord?.student?.id}
                    teacherCourseId={teacherCourseId || editRecord?.course?.id}
                    day={selectedDay !== '' && selectedDay !== null && selectedDay !== undefined 
                        ? selectedDay 
                        : (editRecord?.day !== null && editRecord?.day !== undefined ? Number(editRecord.day) : '')}
                    selectedTime={selectedTime}
                    enabled={enabled}
                    refreshKey={slotRefreshKey}
                    editRecord={editRecord}
                    mode={mode}
                />
            </Grid>

            {/* Start Time Picker */}
            <Grid item xs={12} md={6}>
                <TimePicker
                    label="ساعت شروع"
                    value={selectedTime}
                    onChange={onTimeChange}
                    disabled={selectedDay === '' || selectedDay === null || selectedDay === undefined}
                    helperText={selectedDay === '' || selectedDay === null || selectedDay === undefined ? "ابتدا روز را انتخاب کنید" : "زمان شروع کلاس را انتخاب کنید"}
                />
            </Grid>
        </>
    );
}

